import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpRight,
  BadgeCheck,
  Crown,
  Link2,
  Lock,
  LogOut,
  Megaphone,
  MessageCircle,
  MoreHorizontal,
  Settings,
  Sparkles,
  Users,
} from 'lucide-react';
import { Spinner } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { cn, formatCompact, timeAgo } from '@/lib/utils';
import { ROUTES } from '@/config/constants';
import { recordCommunityVisit } from '../recentCommunities';
import { useLeaveCommunity } from '../hooks/useMyCommunities';
import { MemberFacePile } from './MemberFacePile';
import type { MyCommunity } from '../api/myCommunities';

const MENU_ITEM =
  'flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-[13px] font-semibold text-mid transition-colors hover:bg-white/5 hover:text-hi disabled:pointer-events-none disabled:opacity-50';

interface MyCommunityCardProps {
  item: MyCommunity;
}

/**
 * Explore-card shape (vertical glass card, banner on top) carrying the My
 * Communities info: relationship + status badges, latest message / announcement,
 * a member face-pile, and per-community management actions.
 */
export function MyCommunityCard({ item }: MyCommunityCardProps) {
  const navigate = useNavigate();
  const leave = useLeaveCommunity();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  const { community, relationship, isPrivate, founding, members, lastMessage, announcement } = item;
  const isOwner = relationship === 'owner';
  const isVerified = community.id === 'winr-official' || community.privacy === 'verified';
  const memberCount = community.member_seq || 1;

  const open = () => {
    recordCommunityVisit(community.id);
    navigate(ROUTES.community(community.id));
  };

  const copyLink = () => {
    void navigator.clipboard
      ?.writeText(`${window.location.origin}${ROUTES.community(community.id)}`)
      .then(() => toast('Invite link copied', 'ok'))
      .catch(() => toast('Could not copy link', 'error'));
    setMenuOpen(false);
  };

  const onLeave = () => {
    if (!confirmLeave) {
      setConfirmLeave(true);
      return;
    }
    leave.mutate(community.id, {
      onSuccess: () => toast(`Left ${community.name}`, 'ok'),
      onError: () => toast('Could not leave community', 'error'),
    });
    setMenuOpen(false);
    setConfirmLeave(false);
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setConfirmLeave(false);
  };

  return (
    <div className="group relative flex h-full flex-col p-4 transition-colors duration-200 hover:bg-white/[0.03] after:pointer-events-none after:absolute after:-right-3 after:top-1/4 after:h-1/2 after:w-px after:bg-white/10 max-sm:after:hidden sm:max-lg:[&:nth-child(2n)]:after:hidden lg:max-xl:[&:nth-child(4n)]:after:hidden xl:[&:nth-child(5n)]:after:hidden">
      {/* Banner — opens the community. */}
      <button
        onClick={open}
        aria-label={`Open ${community.name}`}
        className="relative mb-4 aspect-[3/4] w-full flex-none overflow-hidden rounded-md"
      >
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[800ms] ease-cinematic group-hover:scale-[1.06]"
          style={
            community.banner
              ? { backgroundImage: `url(${community.banner})` }
              : { background: `linear-gradient(150deg, ${community.color}33 0%, transparent 62%)` }
          }
        />
        {isPrivate && (
          <span className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-full bg-black/55 text-white/90 backdrop-blur-sm">
            <Lock size={14} />
          </span>
        )}
      </button>

      {/* Body */}
      <div className="flex min-w-0 flex-1 flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-1.5">
          {isOwner ? (
            <StatusIcon label="Owner" tone="accent" icon={<Crown size={13} />} />
          ) : (
            <StatusIcon label="Member" tone="neutral" icon={<Users size={13} />} />
          )}
          {isPrivate && (
            <StatusIcon label="Private" tone="neutral" icon={<Lock size={13} />} />
          )}
          {isVerified && (
            <StatusIcon label="Verified" tone="accent" icon={<BadgeCheck size={13} />} />
          )}
          {founding && (
            <StatusIcon label="Founding member" tone="warning" icon={<Sparkles size={13} />} />
          )}
          {announcement && (
            <StatusIcon label="Announcement" tone="accent" icon={<Megaphone size={13} />} />
          )}
        </div>

        <h3 className="truncate text-xl font-extrabold leading-[1.2] tracking-[-0.4px] text-white max-[600px]:text-[19px]">
          {community.name}
        </h3>

        {/* Latest activity — the "latest messages" / PSA signal. */}
        {lastMessage ? (
          <div className="flex items-center gap-2 text-[13.5px] text-mid">
            {lastMessage.isAnnouncement ? (
              <Megaphone size={15} className="flex-none text-accent" />
            ) : (
              <MessageCircle size={15} className="flex-none text-lo" />
            )}
            <span className="min-w-0 truncate">
              {lastMessage.authorName && (
                <span className="font-semibold text-hi">{lastMessage.authorName}: </span>
              )}
              {lastMessage.body || 'Shared an attachment'}
            </span>
            <span className="flex-none text-lo">· {timeAgo(lastMessage.createdAt)}</span>
          </div>
        ) : (
          <div className="text-[13.5px] text-lo">No messages yet</div>
        )}

        {/* Member faces · count · created */}
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12.5px] font-medium text-lo">
          <MemberFacePile members={members} total={memberCount} />
          <span className="text-mid">{formatCompact(memberCount)} members</span>
          <span className="text-lo/50">•</span>
          <span>Created {timeAgo(community.created_at)} ago</span>
        </div>

        {/* Footer — actions */}
        <div className="mt-auto flex items-center justify-end gap-2 pt-3">
          <div className="flex items-center gap-2">
            <button
              onClick={open}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white transition-opacity hover:opacity-70"
            >
              Open <ArrowUpRight size={15} />
            </button>

            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                aria-label="More actions"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="grid h-9 w-9 place-items-center rounded-pill border border-card-line text-mid transition-colors hover:bg-white/5 hover:text-hi"
              >
                <MoreHorizontal size={17} />
              </button>

              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={closeMenu} />
                  <div
                    role="menu"
                    className="absolute bottom-full right-0 z-50 mb-2 w-56 overflow-hidden rounded-lg border border-card-line bg-elev p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.5)] backdrop-blur-2xl"
                  >
                    <button role="menuitem" className={MENU_ITEM} onClick={open}>
                      <ArrowUpRight size={15} /> Open community
                    </button>
                    <button role="menuitem" className={MENU_ITEM} onClick={copyLink}>
                      <Link2 size={15} /> Copy invite link
                    </button>
                    {isOwner ? (
                      <button role="menuitem" className={MENU_ITEM} onClick={open}>
                        <Settings size={15} /> Manage settings
                      </button>
                    ) : (
                      <button
                        role="menuitem"
                        className={cn(
                          MENU_ITEM,
                          'text-red-300 hover:bg-red-500/10 hover:text-red-200',
                        )}
                        onClick={onLeave}
                        disabled={leave.isPending}
                      >
                        {leave.isPending ? (
                          <Spinner className="h-[15px] w-[15px]" />
                        ) : (
                          <LogOut size={15} />
                        )}
                        {confirmLeave ? 'Tap again to leave' : 'Leave community'}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusIcon({
  label,
  tone,
  icon,
}: {
  label: string;
  tone: 'neutral' | 'accent' | 'warning';
  icon: React.ReactNode;
}) {
  return (
    <span
      title={label}
      aria-label={label}
      className={cn(
        'grid h-7 w-7 place-items-center rounded-full border backdrop-blur-sm transition-colors',
        tone === 'accent' && 'border-accent/35 bg-accent/15 text-accent',
        tone === 'warning' && 'border-amber-400/35 bg-amber-400/12 text-amber-300',
        tone === 'neutral' && 'border-white/10 bg-white/[0.05] text-white/65',
      )}
    >
      {icon}
    </span>
  );
}
